import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Button, Alert, Row, Col, Tooltip, Select } from 'antd';
import { QuestionCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import axios from '../../../../helpers/Axios';

import SecLogo from '../../../../utility/unnamed.png';
import SecBuilding from '../../../../assets/files/img/aaa.jpg';

const authHeaders = {
  headers: {
    'X-Gravitee-Api-Key': process.env.REACT_APP_GRAVITEE_API_KEY_AUTHENTICATION,
  },
};

export default function EnrollFiler() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [form] = Form.useForm();

  const state = location.state || {};
  const userK = state?.keycloakProfile || auth.user?.profile || {};
  const companyId = state?.companyId || localStorage.getItem('user_company');

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ visible: false, type: 'info', message: '' });

  const siteKey = '6LezOBklAAAAAK1tW92Hd88LQ0nxkFo4HKF0ELhp';

  const defaultValues = useMemo(() => {
    const firstName = userK?.given_name || '';
    const lastName = userK?.family_name || '';
    const middleName =
      userK?.middle_name ||
      userK?.middlename ||
      userK?.middleName ||
      '';

    const contactAddress =
      userK?.registeredAddress ||
      [
        userK?.lot_block_phase_houseno,
        userK?.unit_room_floor_building,
        userK?.street_name,
        userK?.subdivision_village_zone,
        userK?.town_district,
      ]
        .filter(Boolean)
        .join(', ');

    return {
      first_name: firstName,
      last_name: lastName,
      middle_name: middleName,
      email: userK?.email || '',
      mobile_no: userK?.mobile || userK?.phone_number || '',
      username: userK?.preferred_username || '',
      contact_address: contactAddress || '',
      name: `${firstName} ${lastName}`.trim(),
    };
  }, [userK]);

  useEffect(() => {
    form.setFieldsValue(defaultValues);
  }, [form, defaultValues]);

  const getRecaptchaToken = async () => {
    if (process.env.NODE_ENV === 'development') {
      return 'dev-fake-token';
    }
    if (!window.grecaptcha) return '';

    return new Promise((resolve) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(siteKey, { action: 'enrollFiler' }).then((token) => {
          resolve(token);
        });
      });
    });
  };

  const onFinish = async (values) => {
    setLoading(true);
    setAlert({ visible: false, type: 'info', message: '' });

    const normalizeApiMessage = (raw) => {
      if (typeof raw === 'string') return raw;
      if (Array.isArray(raw)) return raw.join(', ');
      if (raw && typeof raw === 'object') {
        return Object.entries(raw)
          .map(([key, value]) => {
            const safeValue = Array.isArray(value) ? value.join(', ') : value;
            return `${key}: ${safeValue}`;
          })
          .join(', ');
      }
      return 'Something went wrong, please try again.';
    };

    try {
      const token = await getRecaptchaToken();
      if (!token) {
        setAlert({
          visible: true,
          type: 'error',
          message: 'reCAPTCHA validation failed. Please reload and try again.',
        });
        setLoading(false);
        return;
      }

      const verifyRes = await axios.get(
        `https://api.fortisph.space/gateway/authentication/verified_filers/enrolled/verify/`,
        {
          params: { email: values.email },
          headers: { ...authHeaders.headers },
        }
      );

      if (verifyRes.data.message !== 'True') {
        setAlert({
          visible: true,
          type: 'error',
          message: 'Your account is not yet enrolled as a valid company filer.',
        });
        setLoading(false);
        return;
      }

      await axios.post(
        `https://api.fortisph.space/gateway/authentication/verified_filers/register/`,
        {
          email: values.email,
          username: values.username,
          first_name: values.first_name,
          last_name: values.last_name,
          middle_name: values.middle_name,
          mobile_no: values.mobile_no,
          password: userK?.password || values.password1 || userK?.sub || '',
          suffix: userK?.suffix || values.suffix || '',
          title: userK?.title || values.title || '',
          contact_address: values.contact_address,
          alternate_email: '',
          alternate_contact_no: '',
          position: '',
          division: '',
          employee_id: '',
          is_email_changed: false,
          company_id: companyId,
          keycloak_id: userK?.sub || userK?.keycloak_id || '',
          tin: userK?.tin || '',
          birth_date: userK?.birthdate || userK?.birth_date || '',
        },
        authHeaders
      );

      setAlert({
        visible: true,
        type: 'success',
        message: 'Successfully enrolled! Redirecting...',
      });

      setTimeout(() => {
        navigate('/user/successfully-enrolled');
      }, 3000);
    } catch (error) {
      const apiMessage = error.response?.data?.message ?? error.response?.data;
      setAlert({
        visible: true,
        type: 'error',
        message: normalizeApiMessage(apiMessage),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {/* ── Background Image ── */}
      <img
        src={SecBuilding}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
        }}
      />

      {/* ── Dark Gradient Overlay ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(100deg, rgba(0,36,22,0.93) 0%, rgba(0,50,32,0.82) 45%, rgba(0,30,18,0.65) 100%)',
        }}
      />

      {/* ── Main Canvas Layer ── */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Centered Content Wrapper */}
        <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 md:p-12">
          
          {/* Centered Form Card */}
          <div
            className="w-full max-w-4xl bg-white rounded-[20px] p-6 sm:p-10"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.35)' }}
          >
            {/* Header / Branding Inside Card */}
            <header className="flex flex-col items-center text-center mb-6 pb-6 border-b border-gray-100">
              <img
                src={SecLogo}
                alt="SEC Logo"
                style={{ height: 60, width: 'auto', marginBottom: 12 }}
              />
              <h1 className="text-xl md:text-2xl font-extrabold text-[#111827] mb-1">
                Electronic Filing and Submission Tool (eFAST)
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mb-2">
                Formerly known as Online Submission Tool (OST)
              </p>
              <div style={{ display: 'flex', itemsCenter: 'center', gap: 8 }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    padding: '2px 10px',
                    borderRadius: 999,
                    background: 'rgba(15,130,105,0.1)',
                    color: '#0f8269',
                    border: '1px solid rgba(15,130,105,0.2)',
                  }}
                >
                  FILER ENROLLMENT
                </span>
              </div>
            </header>

            {/* Filer Information Subheader */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6 bg-slate-50 p-4 rounded-xl border border-slate-100">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-gray-800 m-0">Filer Information</h3>
                <Tooltip title="Fields are auto-filled from your authenticated eSECURE session where applicable.">
                  <QuestionCircleOutlined style={{ color: '#0f8269' }} />
                </Tooltip>
              </div>

              <p className="text-xs sm:text-sm text-gray-600 m-0 max-w-xl">
                <span className="text-red-500 font-semibold">*** </span>
                Please make sure your <span className="font-semibold text-gray-800">company</span> has already <span className="font-semibold text-gray-800">enrolled</span> in eFAST before proceeding with filer enrollment.
              </p>
            </div>

            {/* Alert Banner */}
            {alert.visible && (
              <Alert
                style={{ marginBottom: 20, borderRadius: 10 }}
                message={alert.message}
                type={alert.type}
                showIcon
                closable
                onClose={() => setAlert({ visible: false })}
              />
            )}

            {/* Enrollment Form */}
            <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
              <Row gutter={[20, 4]}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="last_name"
                    label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Last Name</span>}
                  >
                    <Input disabled style={{ borderRadius: 8, height: 42, background: '#f9fafb' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item
                    name="first_name"
                    label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>First Name</span>}
                  >
                    <Input disabled style={{ borderRadius: 8, height: 42, background: '#f9fafb' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item
                    name="middle_name"
                    label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Middle Name</span>}
                  >
                    <Input disabled style={{ borderRadius: 8, height: 42, background: '#f9fafb' }} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item
                    name="username"
                    label={
                      <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>
                        Username <span style={{ color: '#ef4444' }}>*</span>
                      </span>
                    }
                    rules={[
                      { required: true, message: 'Enter your username' },
                      {
                        pattern: /^[A-Za-z0-9_#?!@$%^*-.]+$/,
                        message: 'Whitespace in username is not allowed.',
                      },
                    ]}
                  >
                    <Input style={{ borderRadius: 8, height: 42 }} placeholder="Enter username" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item
                    name="email"
                    label={
                      <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>
                        Email <span style={{ color: '#ef4444' }}>*</span>
                      </span>
                    }
                    rules={[
                      { required: true, message: 'Enter your email address' },
                      { type: 'email', message: 'Invalid email format!' },
                      {
                        pattern: /^[^,]+$/,
                        message: 'Commas are not allowed.',
                      },
                    ]}
                  >
                    <Input style={{ borderRadius: 8, height: 42 }} placeholder="Enter email address" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                  <Form.Item
                    name="mobile_no"
                    label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Landline / Mobile No.</span>}
                  >
                    <Input disabled style={{ borderRadius: 8, height: 42, background: '#f9fafb' }} />
                  </Form.Item>
                </Col>
              </Row>

              {/* Hidden Inputs */}
              <Form.Item name="title" hidden><Select /></Form.Item>
              <Form.Item name="suffix" hidden><Select /></Form.Item>
              <Form.Item name="contact_address" hidden><Input.TextArea /></Form.Item>
              <Form.Item name="password1" hidden><Input.Password /></Form.Item>
              <Form.Item name="password2" hidden><Input.Password /></Form.Item>

              {/* Card Footer Actions */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justify: 'space-between',
                  width: '100%',
                  marginTop: 20,
                  paddingTop: 20,
                  borderTop: '1px solid #f3f4f6',
                }}
              >
                <Link
                  to="/user/login"
                  style={{
                    color: '#0f8269',
                    fontWeight: 600,
                    fontSize: 14,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <ArrowLeftOutlined /> Back to Sign In
                </Link>
              
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  style={{
                    background: '#0f8269',
                    borderColor: '#0f8269',
                    borderRadius: 10,
                    height: 44,
                    padding: '0 32px',
                    fontWeight: 600,
                    fontSize: 15,
                    marginLeft: 'auto', // Ensures it stays pinned to the right edge
                  }}
                >
                  Submit
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}